import React, { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaLightbulb, FaExternalLinkAlt, FaPlus } from "react-icons/fa";
import API from "@/services/api";
import toast from "react-hot-toast";

const translations = {
  en: {
    title: "Recommended Platforms",
    description: "Discover the best platforms to post your job",
    loading: "Analyzing your job posting...",
    get_recommendations: "Get AI Recommendations",
    general_advice: "General Advice",
    why_post_here: "Why post here",
    posting_tips: "Posting Tips",
    estimated_cost: "Estimated Cost",
    visit_platform: "Visit Platform",
    no_recommendations: "No recommendations available",
    error: "Failed to get recommendations",
    refresh: "Refresh Recommendations",
    use_this_channel: "Use this channel",
  },
  fr: {
    title: "Plateformes recommandées",
    description: "Découvrez les meilleures plateformes pour publier votre offre",
    loading: "Analyse de votre offre en cours...",
    get_recommendations: "Me recommander des canaux",
    general_advice: "Conseils généraux",
    why_post_here: "Pourquoi publier ici",
    posting_tips: "Conseils de publication",
    estimated_cost: "Coût estimé",
    visit_platform: "Visiter la plateforme",
    no_recommendations: "Aucune recommandation disponible",
    error: "Échec de l'obtention des recommandations",
    refresh: "Actualiser les recommandations",
    use_this_channel: "Utiliser ce canal",
  },
};

export default function Recommendations({ job, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(null);

  console.log("Recommendations render state:", { loading, hasSearched, recommendations });

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setHasSearched(true);
      const response = await API.post("/channel/recommendations", { job_id: jobId });
      if (!response.ok) return toast.error(t("error"));

      setRecommendations(response.data);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleUseChannel = async (platform) => {
    try {
      setCreatingChannel(platform.name);

      let channelId = null;
      let channelSlug = null;

      // 1. Search for existing channel
      const searchRes = await API.post("/channel/search", { search: platform.name, limit: 10 });
      if (searchRes.ok && searchRes.data) {
        const existing = searchRes.data.find((c) => c.name.toLowerCase() === platform.name.toLowerCase());
        if (existing) {
          channelId = existing._id;
          channelSlug = existing.slug;
        }
      }

      // 2. Create if not exists
      if (!channelId) {
        const createRes = await API.post("/channel", {
          name: platform.name,
          url: platform.url,
          description: platform.whyPostHere || "",
          published: true,
        });
        if (!createRes.ok) throw new Error("Failed to create channel");
        channelId = createRes.data._id;
        channelSlug = createRes.data.slug;
      }

      // 3. Create post if not exists
      const existingPost = posts.find((p) => p.channel_id === channelId);

      if (!existingPost) {
        const postRes = await API.post("/post", { job_id: jobId, channel_id: channelId });
        if (!postRes.ok) throw new Error("Failed to create job post");
        if (setPosts) {
          setPosts([...posts, postRes.data]);
        }
      }

      // 4. Navigate
      navigate(`/recruiter/job-ads/${jobId}/ad/distribution/${channelSlug}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to setup channel");
    } finally {
      setCreatingChannel(null);
    }
  };

  // Initial State: Show "Get Recommendations" Button
  if (!hasSearched && !loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
          <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaLightbulb className="w-10 h-10 text-yellow-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t("title")}</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">{t("description")}</p>
          <button
            onClick={fetchRecommendations}
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all shadow-md hover:shadow-lg font-bold text-lg cursor-pointer"
          >
            <FaLightbulb className="w-5 h-5" />
            <span>{t("get_recommendations")}</span>
          </button>
        </div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600 text-lg">{t("loading")}</p>
          <p className="text-gray-500 text-sm mt-2">This may take a few seconds...</p>
        </div>
      </div>
    );
  }

  // Results State
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header with Refresh Button */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
            <FaLightbulb className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600">{t("description")}</p>
          </div>
        </div>
        <button
          onClick={fetchRecommendations}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm font-medium hover:border-gray-300 cursor-pointer"
        >
          <FaLightbulb className="w-4 h-4 text-yellow-500" />
          <span>{t("refresh")}</span>
        </button>
      </div>

      {/* General Advice */}
      {recommendations?.generalAdvice && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <FaLightbulb className="w-5 h-5" />
            {t("general_advice")}
          </h3>
          <p className="text-blue-800 whitespace-pre-wrap">{recommendations.generalAdvice}</p>
        </div>
      )}

      {/* Recommended Platforms */}
      {recommendations?.rankedPlatforms && recommendations.rankedPlatforms.length > 0 ? (
        <div className="space-y-4">
          {recommendations.rankedPlatforms.map((platform, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                {/* Rank Badge */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">#{platform.rank}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{platform.name}</h3>
                      {platform.estimatedCost && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">{t("estimated_cost")}:</span> {platform.estimatedCost}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUseChannel(platform)}
                        disabled={creatingChannel === platform.name}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        {creatingChannel === platform.name ? (
                          <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <FaPlus className="w-3 h-3" />
                        )}
                        <span>{t("use_this_channel")}</span>
                      </button>

                      <a
                        href={platform.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                      >
                        {t("visit_platform")}
                        <FaExternalLinkAlt className="w-3 h-3" />
                      </a>
                    </div>
                  </div>

                  {/* Why Post Here */}
                  {platform.whyPostHere && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("why_post_here")}</h4>
                      <p className="text-gray-600 text-sm">{platform.whyPostHere}</p>
                    </div>
                  )}

                  {/* Posting Tips */}
                  {platform.postingTips && platform.postingTips.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("posting_tips")}</h4>
                      <ul className="space-y-1">
                        {platform.postingTips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-primary mt-0.5">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500">{t("no_recommendations")}</p>
        </div>
      )}
    </div>
  );
}
